import assertNever from "assert-never";
import ons from "onsenui";
import React from "react";
import { APIConfig } from "../../../API";
import { AppBottomSheet } from "../../../components/Generic/AppBottomSheet";
import { AppButton } from "../../../components/Generic/AppButton";
import { AppList, AppListItem } from "../../../components/Generic/AppList";
import { AppOverlay } from "../../../components/Generic/AppOverlay";
import Flex from "../../../components/Generic/Flex";
import { isDefined } from "../../../components/Generic/IsDefined";
import * as LocalStore from "../../../LocalStore";
import { createBookmarkToAdd } from "../../../ServerSelection";

const defaultBookmarks: LocalStore.APIConfigBookmark[] = [
  { title: "Production", apiConfig: LocalStore.productionApiConfig },
  { title: "Staging", apiConfig: LocalStore.stagingApiConfig },
  { title: "Local", apiConfig: LocalStore.localApiConfig },
  { title: "P", apiConfig: LocalStore.pApiConfig },
];

interface Props {
  isOpen: boolean;
  onChangeApiConfig: (apiConfig: APIConfig) => void;
  onLoginViaAuthToken: (authToken: string, apiConfig: APIConfig) => void;
  onRequestClose: () => void;
}

type State =
  | {
      readonly type: "Loading";
    }
  | {
      readonly type: "Ready";
      apiConfig: APIConfig;
      bookmarks: LocalStore.APIConfigBookmark[];
    };

const DevToolsBottomSheet = (props: Props) => {
  const [state, setState] = React.useState<State>({ type: "Loading" });

  const handleClickAddBookmark = async () => {
    const bookmark = await createBookmarkToAdd();

    if (isDefined(bookmark)) {
      await LocalStore.addAPIBookmark(bookmark);

      setState((v) => {
        if (v.type === "Ready") {
          return {
            ...v,
            bookmarks: [...v.bookmarks, bookmark],
            apiConfig: bookmark.apiConfig,
          };
        }

        return v;
      });
    }
  };

  const handleClickResetBookmarks = () => {
    const defaultApiConfig = LocalStore.productionApiConfig;

    LocalStore.clearAPIBookmarks();
    props.onChangeApiConfig.call(null, defaultApiConfig);

    setState((v) => ({
      ...v,
      bookmarks: defaultBookmarks,
      apiConfig: defaultApiConfig,
    }));
  };

  const handleClickAuthToken = async () => {
    if (state.type === "Loading") {
      return;
    }

    const authToken = (await ons.notification.prompt(" ", {
      animation: "none",
      modifier: "material",
      cancelable: true,
      title: "Enter authToken",
    })) as unknown as string;

    props.onLoginViaAuthToken.call(null, authToken, state.apiConfig);
  };

  const handleChangeAPIConfig = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (state.type === "Loading") {
      return;
    }

    const apiBaseUrl = e.target.value;
    const bookmark = state.bookmarks.find((x) => x.apiConfig.apiBaseUrl === apiBaseUrl);

    if (isDefined(bookmark)) {
      props.onChangeApiConfig.call(null, bookmark.apiConfig);
      setState((v) => ({ ...v, apiConfig: bookmark.apiConfig }));
    }
  };

  const resolveStateDeps = React.useCallback(async () => {
    const bookmarks = await LocalStore.getAPIBookmarks();
    const apiConfig = await LocalStore.loadAPIConfig();

    setState({
      type: "Ready",
      bookmarks: [...defaultBookmarks, ...bookmarks],
      apiConfig: apiConfig,
    });
  }, []);

  React.useEffect(() => {
    resolveStateDeps();
  }, [resolveStateDeps]);

  switch (state.type) {
    case "Loading":
      return <></>;
    case "Ready":
      return (
        <AppOverlay>
          <AppBottomSheet
            title="MedFlyt DevTools"
            isOpen={props.isOpen}
            onRequestClose={props.onRequestClose}
            rounded={true}
          >
            <AppList bordered={true}>
              <AppListItem
                title={<strong>Environment</strong>}
                trailing={
                  <select value={state.apiConfig.apiBaseUrl} onChange={handleChangeAPIConfig}>
                    {state.bookmarks.map((bookmark) => (
                      <option
                        key={bookmark.apiConfig.apiBaseUrl}
                        value={bookmark.apiConfig.apiBaseUrl}
                      >
                        {bookmark.title}
                      </option>
                    ))}
                  </select>
                }
              />
              <AppListItem
                title={<em>apiBaseUrl</em>}
                trailing={<i>{state.apiConfig.apiBaseUrl}</i>}
              />
              <AppListItem
                title={<em>socketIOServerUrl</em>}
                trailing={<i>{state.apiConfig.socketIOServerUrl}</i>}
              />
            </AppList>

            <div style={{ marginTop: 16 }}>
              <Flex xGap={12} stretchItems={true}>
                <AppButton size="small" onClick={handleClickAuthToken}>
                  (AuthToken)
                </AppButton>
                <AppButton size="small" variant="outline" onClick={handleClickAddBookmark}>
                  Add
                </AppButton>
                <AppButton size="small" variant="outline" onClick={handleClickResetBookmarks}>
                  Reset
                </AppButton>
              </Flex>
            </div>
          </AppBottomSheet>
        </AppOverlay>
      );
    default:
      assertNever(state);
  }
};

export default DevToolsBottomSheet;
